import * as React from 'react';

import useMergedRef from '@react-hook/merged-ref';
import { Portal } from 'react-portal';
import styled from 'styled-components';

import { useClickOutside } from '../../../ModernScripts/Spotlight/Hooks/useClickOutside';
import { camelToKebab } from '../../../Scripts/Spotlight/Components/CamelToKebab';
import { COLOR } from '../../packages/constants';
import { SearchFilter } from '../search-filter/search-filter';

export interface Option {
    value: string;
    text: string;
    dataId?: string;
    flagColour?: string; // little highlight colour
    disabled?: boolean;
    icon?: React.ReactElement;
}

interface ComponentWithDensityProps {
    density: Density;
}

interface StyledOptionProps {
    interactionMode: InteractionModes;
}

interface StyledOptionLabelInnerProps extends ComponentWithDensityProps {
    interactionMode: InteractionModes;
    flagColour?: string; // little highlight colour
    disable?: boolean;
}

export interface OptionGroup {
    label: string;
    options: Option[];
}

export interface Section {
    type: 'section';
    options: Array<Option | OptionGroup>;
}

export type MenuData = Array<Option | OptionGroup | Section>;

export type Density = 'standard' | 'spacious';

export type FontWeight = 'normal' | 'semi-bold';

interface StyledMenuListProps extends ComponentWithDensityProps {
    ref: any;
    fontWeight: FontWeight;
}

interface StyledMenuOptionsListProps {
    hasFilter?: boolean;
}

export interface MenuListProps {
    id?: string;
    menuType?: MenuTypes;
    options: MenuData;
    node?: Element;
    setChosenItem: ({ value, scrollY }: { value: string; scrollY?: number }) => void;
    interactionMode: InteractionModes;
    setInteractionMode: any;
    /**
     * Optional callback for when the menu is closed, will be called with the active item.
     * Note: this shouldn't be relied on for confirming a selection.
     * The menu can be closed with an active item and not have had a selection made.
     */
    onClose?: ({ value }: { value?: string }) => void;
    focussedInputValue: React.MutableRefObject<string | undefined>;
    activeItem?: string;
    setActiveItem: (activeItem: string) => void;
    menuScrollY?: React.MutableRefObject<number>;
    dataId: string;
    hasFilter?: boolean;
    filterValue?: string;
    setFilterValue?: (value: string) => void;
    isFilterFocussed?: boolean;
    setIsFilterFocussed?: (value: boolean) => void;
    style?: React.CSSProperties;
    headerText?: React.ReactNode;
    density?: Density;
    fontWeight?: FontWeight;
}

export enum MenuTypes {
    /* behave like an HTML <select>, updates selection while menu is open on key navigation */
    Select = 'select',
    /* behave like a navigation menu, only make the selection on item click or enter key */
    NavMenu = 'navmenu',
}

export enum InteractionModes {
    Mouse = 'mouse',
    Keyboard = 'keyboard',
}

const StyledMenuList = styled.div<StyledMenuListProps>`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    border: 1px solid #d9dee6;
    border-radius: 5px;
    background: #fff;
    box-shadow: 0px 1px 2px 1px rgba(0, 0, 0, 0.08);
    font-size: ${(props) => (props.density === 'spacious' ? '14px' : '13px')};
    font-weight: ${(props) => (props.fontWeight === 'semi-bold' ? '600' : '400')};
`;

const StyledSearchFilter = styled(SearchFilter)`
    margin: 4px;
`;

const StyledMenuOptionsList = styled.ul<StyledMenuOptionsListProps>`
    position: relative;
    overflow: auto;
    list-style-type: none;
    ${(props) => (props.hasFilter ? `padding-top: 2px;` : `padding-top: 6px;`)}
    padding-bottom: 6px;
`;

const StyledOptionGroup = styled.li`
    margin-top: 5px;
    margin-bottom: 5px;
`;

const StyledOptionGroupHeading = styled.span<ComponentWithDensityProps>`
    display: block;
    padding-top: ${(props) => (props.density === 'spacious' ? '10px' : '6px')};
    padding-bottom: ${(props) => (props.density === 'spacious' ? '10px' : '6px')};
    padding-left: ${(props) => (props.density === 'spacious' ? '20px' : '16px')};
    padding-right: ${(props) => (props.density === 'spacious' ? '20px' : '16px')};
    line-height: 1.42;
    font-weight: bold;
    color: ${COLOR.MAIN.DARK_GREY};
`;

const StyledOptionGroupOptionsList = styled.ul`
    list-style-type: none;
`;

const StyledOption = styled.li`
    position: relative;
    margin: 0px;
    padding: 0px;
`;

const NoResultsText = styled.div`
    display: block;
    padding-top: 6px;
    padding-bottom: 6px;
    padding-left: 16px;
    padding-right: 16px;
    line-height: 1.42;
    color: #031c44;
`;

const StyledOptionLabel = styled.label`
    display: block;
    padding: 0px;
    margin: 0px;
`;

const StyledOptionLabelInner = styled.div<StyledOptionLabelInnerProps>`
    display: flex;
    align-items: flex-start;
    position: relative;
    padding-top: ${(props) => (props.density === 'spacious' ? '10px' : '6px')};
    padding-bottom: ${(props) => (props.density === 'spacious' ? '10px' : '6px')};
    padding-left: ${(props) => (props.density === 'spacious' ? '20px' : '16px')};
    padding-right: ${(props) => (props.density === 'spacious' ? '20px' : '16px')};
    cursor: ${(props) => (props.disable ? 'not-allowed' : 'pointer')};
    line-height: 1.42;
    color: ${(props) => (props.disable ? COLOR.DROPDOWN.DROPDOWN_DISABLED_OPTION_TEXT : '#031c44')};
    ${(props) =>
        props.interactionMode === InteractionModes.Mouse &&
        !props.disable &&
        `        
    &:hover {
        background: #f3f4f7;
    }
    `}

    ${(props) =>
        props.flagColour &&
        `&:before {
        content: '';
        position: absolute;
        top: 0px;
        bottom: 1px;
        left: 0px;
        width: 4px;
        background: ${props.flagColour};
    }`}
`;

const StyledHeaderRow = styled.div<ComponentWithDensityProps>`
    padding-top: ${(props) => (props.density === 'spacious' ? '10px' : '6px')};
    padding-bottom: ${(props) => (props.density === 'spacious' ? '10px' : '6px')};
    padding-left: ${(props) => (props.density === 'spacious' ? '20px' : '16px')};
    padding-right: ${(props) => (props.density === 'spacious' ? '20px' : '16px')};
    line-height: 1.42;
    background: #f3f4f7;
    color: #435c84;
    border-bottom: 1px solid #cdd3de;
    border-radius: 4px 4px 0px 0px;
    display: flex;
    align-items: center;
    word-break: break-word;
`;

const StyledOptionLabelIcon = styled.div`
    display: flex;
    flex-shrink: 0;
    margin-right: 8px;
`;

const StyledOptionLabelText = styled.span`
    align-self: center;
`;

const StyledOptionRadio = styled.input<StyledOptionProps>`
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: -1;

    ${(props) =>
        props.interactionMode === InteractionModes.Keyboard &&
        `
        &:checked ~ ${StyledOptionLabelInner} {
            background: #f3f4f7;
        }
    `}

    .assistfocus &:focus ~ ${StyledOptionLabelInner} {
        outline: 1px solid black;
        outline-offset: -1px;
    }
`;

const StyledSectionList = styled.ul`
    list-style-type: none;
`;

interface StyledSectionProps {
    isFirstItem: boolean;
    isAfterSection: boolean;
    isLastItem: boolean;
    onlyItem: boolean;
}

const StyledSection = styled.li<StyledSectionProps>`
    border-top: 1px solid #cdd3de;
    border-bottom: 1px solid #cdd3de;
    margin: 6px 0 6px 0;
    padding: 6px 0 6px 0;

    ${(props) => (props.isFirstItem || props.isAfterSection) && 'border-top: none; margin-top: 0; padding-top: 0;'}
    ${(props) => props.isLastItem && 'border-bottom: none; margin-bottom: 0; padding-bottom: 0;'}
    ${(props) => props.onlyItem && 'border-bottom: none; border-top: none; margin: 0; padding: 0;'}

    li:last-child {
        margin-bottom: 0;
    }
`;

function isOptionGroup(arg: any): arg is OptionGroup {
    return typeof arg.label !== 'undefined' && Array.isArray(arg.options);
}

const isSection = (arg: any): arg is Section => {
    return arg?.type === 'section';
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noOp = () => {};

interface OptionRowProps extends ComponentWithDensityProps {
    option: Option;
    dataId: string;
    interactionMode: InteractionModes;
    active: boolean;
    menuName: string;
    mouseMoveHandler: React.MouseEventHandler;
    mouseDownHandler: React.MouseEventHandler;
    clickHandler: React.MouseEventHandler;
}

const OptionRow = ({
    option,
    dataId,
    interactionMode,
    active,
    menuName,
    density,
    mouseMoveHandler,
    mouseDownHandler,
    clickHandler,
}: OptionRowProps) => {
    return (
        <StyledOption
            onMouseDown={mouseDownHandler}
            data-test-id={`${dataId}__li-${camelToKebab(option.dataId || option.text)}`}
        >
            <StyledOptionLabel
                className={'js-menu-option-label'}
                onMouseMove={mouseMoveHandler}
                data-test-id={`${dataId}__label-${camelToKebab(option.text)}`}
            >
                <StyledOptionRadio
                    data-test-id={`${dataId}__radio-${camelToKebab(option.text)}`}
                    type="radio"
                    name={menuName}
                    value={option.value}
                    interactionMode={interactionMode}
                    checked={active}
                    onClick={option.disabled ? undefined : clickHandler}
                    readOnly={true}
                    onKeyDown={(e) => e.preventDefault()}
                    {...(option.disabled && { disabled: true })}
                />
                <StyledOptionLabelInner
                    flagColour={option.flagColour}
                    interactionMode={interactionMode}
                    disable={option.disabled}
                    density={density}
                >
                    {!!option.icon && <StyledOptionLabelIcon>{option.icon}</StyledOptionLabelIcon>}
                    <StyledOptionLabelText>{option.text}</StyledOptionLabelText>
                </StyledOptionLabelInner>
            </StyledOptionLabel>
        </StyledOption>
    );
};

interface SectionItemProps extends ComponentWithDensityProps {
    item: Section;
    index: number;
    options: MenuData;
    dataId: string;
    interactionMode: InteractionModes;
    activeItem: string | undefined;
    menuName: string;
    mouseMoveOnItem: React.MouseEventHandler;
    mouseDownOnItem: React.MouseEventHandler;
    clickOptionHandler: React.MouseEventHandler;
}

const OptionGroupItem = ({
    item,
    index,
    dataId,
    interactionMode,
    activeItem,
    menuName,
    density,
    mouseMoveOnItem,
    mouseDownOnItem,
    clickOptionHandler,
}: OptionGroupProps) => {
    return (
        <StyledOptionGroup key={`${item.label}-${index}`}>
            <StyledOptionGroupHeading className={'js-menu-option-group-heading'} density={density}>
                {item.label}
            </StyledOptionGroupHeading>
            {item.options && (
                <StyledOptionGroupOptionsList>
                    {item.options.map((option) => (
                        <OptionRow
                            key={option.value}
                            option={option}
                            dataId={dataId}
                            interactionMode={interactionMode}
                            active={option.value === activeItem}
                            menuName={menuName}
                            density={density}
                            mouseMoveHandler={mouseMoveOnItem}
                            mouseDownHandler={mouseDownOnItem}
                            clickHandler={clickOptionHandler}
                        />
                    ))}
                </StyledOptionGroupOptionsList>
            )}
        </StyledOptionGroup>
    );
};

const SectionItem = ({
    item,
    index,
    options,
    dataId,
    interactionMode,
    activeItem,
    menuName,
    density,
    mouseMoveOnItem,
    mouseDownOnItem,
    clickOptionHandler,
}: SectionItemProps) => {
    return (
        <StyledSection
            isFirstItem={index === 0 && options.length !== 1}
            isAfterSection={isSection(options[index - 1])}
            isLastItem={index === options.length - 1 && options.length !== 1}
            onlyItem={options.length === 1}
        >
            <StyledSectionList>
                {item.options.map((option, ind) => {
                    if (isOptionGroup(option)) {
                        return (
                            <OptionGroupItem
                                key={option.label + '-' + ind}
                                item={option}
                                index={ind}
                                dataId={dataId}
                                interactionMode={interactionMode}
                                activeItem={activeItem}
                                menuName={menuName}
                                density={density}
                                mouseDownOnItem={mouseDownOnItem}
                                mouseMoveOnItem={mouseMoveOnItem}
                                clickOptionHandler={clickOptionHandler}
                            />
                        );
                    }
                    if (option.value) {
                        return (
                            <OptionRow
                                key={option.value}
                                option={option}
                                dataId={dataId}
                                interactionMode={interactionMode}
                                active={option.value === activeItem}
                                menuName={menuName}
                                density={density}
                                mouseMoveHandler={mouseMoveOnItem}
                                mouseDownHandler={mouseDownOnItem}
                                clickHandler={clickOptionHandler}
                            />
                        );
                    }
                })}
            </StyledSectionList>
        </StyledSection>
    );
};

interface OptionGroupProps extends ComponentWithDensityProps {
    item: OptionGroup;
    index: number;
    dataId: string;
    interactionMode: InteractionModes;
    activeItem: string | undefined;
    menuName: string;
    mouseMoveOnItem: React.MouseEventHandler;
    mouseDownOnItem: React.MouseEventHandler;
    clickOptionHandler: React.MouseEventHandler;
}

interface MenuOptionsProps extends ComponentWithDensityProps {
    dataId: string;
    options: MenuData;
    interactionMode: InteractionModes;
    mouseMoveOnItem: React.MouseEventHandler;
    clickOptionHandler: React.MouseEventHandler;
    activeItem?: string;
    mouseDownOnItem: React.MouseEventHandler;
    menuName: string;
}

const MenuOptions = ({
    dataId,
    options,
    interactionMode,
    mouseMoveOnItem,
    clickOptionHandler,
    activeItem,
    mouseDownOnItem,
    menuName,
    density,
}: MenuOptionsProps) => {
    return (
        <>
            {options.map((item, ii) => {
                if (isSection(item)) {
                    return (
                        <SectionItem
                            key={item.type + '-' + ii}
                            item={item}
                            index={ii}
                            options={options}
                            dataId={dataId}
                            interactionMode={interactionMode}
                            activeItem={activeItem}
                            menuName={menuName}
                            density={density}
                            mouseDownOnItem={mouseDownOnItem}
                            mouseMoveOnItem={mouseMoveOnItem}
                            clickOptionHandler={clickOptionHandler}
                        />
                    );
                }
                if (isOptionGroup(item)) {
                    return (
                        <OptionGroupItem
                            key={item.label + '-' + ii}
                            item={item}
                            index={ii}
                            dataId={dataId}
                            interactionMode={interactionMode}
                            activeItem={activeItem}
                            menuName={menuName}
                            density={density}
                            mouseDownOnItem={mouseDownOnItem}
                            mouseMoveOnItem={mouseMoveOnItem}
                            clickOptionHandler={clickOptionHandler}
                        />
                    );
                }
                if (item.value) {
                    return (
                        <OptionRow
                            key={item.value}
                            option={item}
                            dataId={dataId}
                            interactionMode={interactionMode}
                            active={item.value === activeItem}
                            menuName={menuName}
                            density={density}
                            mouseMoveHandler={mouseMoveOnItem}
                            mouseDownHandler={mouseDownOnItem}
                            clickHandler={clickOptionHandler}
                        />
                    );
                }
            })}
        </>
    );
};

/**
 * get the menuScrollY needed to bring the item in to view
 */
const getScrollToItem = (
    /** the radio input of the menu item */
    item: HTMLInputElement,
    /** the menu's current vertical scroll */
    menuScrollY: number,
    /** the menu's list of options (scrollable area) */
    menuOptionsRef: React.MutableRefObject<HTMLUListElement | undefined>,
) => {
    const liEl = item.parentElement?.parentElement; // get the radio input's parent list item element
    const offsetTop = liEl?.offsetTop;
    const menuHeight = menuOptionsRef.current?.clientHeight;
    const optionHeight = 30;
    const itemHeight = item.parentElement?.clientHeight || optionHeight;
    // is the item out of view (at least partially)
    const isItemAboveView = typeof offsetTop !== 'undefined' && offsetTop < menuScrollY;
    // is the item out of view (at least partially)
    const isItemBelowView =
        typeof menuHeight !== 'undefined' &&
        typeof offsetTop !== 'undefined' &&
        offsetTop + itemHeight > menuScrollY + menuHeight;
    if (isItemBelowView) {
        return menuScrollY + itemHeight;
    } else if (isItemAboveView) {
        return menuScrollY - itemHeight;
    }
    return menuScrollY;
};

/**
 * A list of items for a menu, for instance is used by dropdown-menu and select-menu
 */
export const MenuList = React.forwardRef<HTMLElement, MenuListProps>(
    (
        {
            id = 'drop-menu',
            menuType = MenuTypes.Select,
            setChosenItem,
            activeItem,
            setActiveItem,
            options,
            node = document.body,
            onClose,
            interactionMode,
            setInteractionMode,
            focussedInputValue,
            menuScrollY,
            dataId,
            hasFilter,
            filterValue,
            setFilterValue = noOp,
            isFilterFocussed,
            setIsFilterFocussed = noOp,
            style,
            headerText,
            density = 'standard',
            fontWeight = 'normal',
            ...props
        },
        ref,
    ) => {
        /** the menu container */
        const menuRef = React.useRef<HTMLElement>();
        /** the menu's search filter input */
        const filterInputRef = React.useRef<HTMLInputElement>();
        /** the menu's list of options (scrollable area) */
        const menuOptionsRef = React.useRef<HTMLUListElement>();

        /**
         * field name used for radios
         */
        const menuName = 'drop-menu';
        /**
         * menu id
         */
        const menuId = id;
        /**
         * menu list id
         */
        const menuListId = `${menuId}-list`;
        /**
         * unique key for the portal
         */
        const portalKey = 'drop-menu-portal';

        const arrowUp = 'ArrowUp';
        const arrowDown = 'ArrowDown';
        const arrowLeft = 'ArrowLeft';
        const arrowRight = 'ArrowRight';

        const arrowsNext = [arrowDown, arrowRight];
        const arrowsPrev = [arrowUp, arrowLeft];

        /**
         * Sets the menu item cursor and also updates the selected value.
         * Uses the menuType and passed selectionTypes to know what type of menu the selected value should be updated for.
         *
         * Selections can be made in different ways and may not want it to update the selected value.
         * e.g. A nav menu requires a more deliberate click or enter key to determine the choice.
         * Whereas a select updates as the user navigates the list.
         */
        const selectItem = ({ value, selectionTypes }: { value: string; selectionTypes?: MenuTypes[] }) => {
            // set the item to highlight (the menu item cursor)
            setActiveItem(value);
            // update the selection based on the menuType and how the selection was made
            if (menuType === MenuTypes.Select) {
                if (value && selectionTypes?.includes(MenuTypes.Select)) {
                    const menuEl = document.getElementById(menuListId);
                    const menuScrollTop = menuEl?.scrollTop;
                    setChosenItem({ value, scrollY: menuScrollTop });
                }
            } else if (menuType === MenuTypes.NavMenu) {
                // nav menu only confirms selection when it is made in particular way (click and enter key)
                if (value && selectionTypes?.includes(MenuTypes.NavMenu)) {
                    setChosenItem({ value });
                }
            }
        };

        const setFocussedInputValue = (value: string) => {
            focussedInputValue.current = value;
        };

        /**
         * Close the menu by calling a provided onClose callback
         */
        const close = ({ value }: { value?: string }) => {
            setFilterValue('');
            // reset the interaction mode so focus highlights work correctly
            setInteractionMode(InteractionModes.Keyboard);
            // focus the value or active item
            const focusVal = value || activeItem || '';
            setFocussedInputValue(focusVal);

            if (typeof onClose === 'function') {
                if (typeof value !== 'undefined') {
                    onClose({ value });
                } else {
                    onClose({ value: undefined });
                }
            }
        };

        /**
         * Handles updating the selected item and close the menu.
         * A value can be passed without confirming the selection.
         * Only pass selection types when the selection should be confirmed for that menu type.
         */
        const selectAndClose = ({ value, selectionTypes }: { value?: string; selectionTypes?: MenuTypes[] }) => {
            if (typeof value !== 'undefined') {
                selectItem({ value, selectionTypes });
            }
            close({ value });
        };

        /**
         * Controls navigating the menu.
         * If at the end, don't navigate next.
         * If at the start don't navigate prev.
         * Navigate next/prev based on arrow input.
         * On navigation with arrow input skip disabled option and set focus on next available.
         * Set the focus to the next/prev item in the list.
         */
        const navigateItem = (e: any) => {
            const radios = Array.from(document.querySelectorAll<HTMLInputElement>(`[name="${menuName}"]:enabled`));
            const focussedRadioVal = focussedInputValue.current || (radios.length && radios[0].value) || '';
            const focussedInputEl = document.querySelector<HTMLInputElement>(
                `[name="${menuName}"][value="${focussedRadioVal}"]`,
            );
            const index = (focussedInputEl && radios.indexOf(focussedInputEl)) || 0;
            const activeInputEl = document.querySelector<HTMLInputElement>(
                `[name="${menuName}"][value="${activeItem}"]`,
            );

            const dir = (() => {
                if (arrowsPrev.includes(e.key) && index > 0) {
                    return -1;
                }
                if (arrowsNext.includes(e.key) && index < radios.length - 1) {
                    return 1;
                }
                // do nothing
                return 0;
            })();

            const isActiveItemVisible = !!activeInputEl;

            const offset = (() => {
                // if no item and activeItem is first radio or active item is not visible, set to 0
                if (index === 0 && (!activeItem || !isActiveItemVisible)) {
                    return 0;
                }
                return dir;
            })();

            const newItem = radios[index + offset]; // + or - for prev/next

            if (newItem) {
                setFocussedInputValue(newItem.value);
                selectItem({ value: newItem.value, selectionTypes: [MenuTypes.Select] });
            }

            setInteractionMode(InteractionModes.Keyboard);
        };

        const mouseDownHandler = (e: any) => {
            // focus the item we moused down on
            const inputEl = e.target.closest('label').querySelector('input');
            setInteractionMode(InteractionModes.Mouse);
            setFocussedInputValue(inputEl.value);
        };

        const clickOptionHandler = (e: any) => {
            /*
             * Here we check the interaction mode is not keyboard, as radio button change will fire a click event regardless
             * and we don't want that to actually confirm the selection and close the menu.
             *
             * NOTE: if we key to an item while hovering another item, then click - it's not selecting the clicked item
             * current work around is to rely on mouseMove to change the interaction mode.
             */
            if (interactionMode === InteractionModes.Keyboard) {
                return;
            }
            // the option was clicked while in a mouse interaction mode, so confirm the selection and close
            selectAndClose({ value: e.target.value, selectionTypes: [MenuTypes.Select, MenuTypes.NavMenu] });
        };

        const focusElementAndMaintainScrollPosition = (el: any) => {
            // get window scroll
            const scrollX = window.scrollX;
            const scrollY = window.scrollY;
            el.focus({ preventScroll: true });
            // restore scroll of window
            window.scrollTo(scrollX, scrollY);
        };

        const keyDown = (e: React.KeyboardEvent) => {
            const navArrows = [arrowUp, arrowDown];
            const arrows = [arrowUp, arrowDown, arrowLeft, arrowRight];
            const selectKeys = ['Enter'];
            const exitKeys = ['Escape'];
            const tabKey = 'Tab';

            const focussedEl = document.activeElement;
            const focusIsFilterInput = focussedEl === filterInputRef.current;
            const focusIsMenuOptions = focussedEl === menuOptionsRef.current;
            const focusIsChildOfMenuOptions = menuOptionsRef.current?.contains(focussedEl as Node);
            const activeInputEl = document.querySelector<HTMLInputElement>(
                `[name="${menuName}"][value="${activeItem}"]`,
            );
            const isActiveItemVisible = !!activeInputEl;

            // exit
            if (exitKeys.includes(e.key)) {
                if (menuType === MenuTypes.Select) {
                    // select the active item for a select type dropdown
                    selectAndClose({ value: activeItem, selectionTypes: [MenuTypes.Select] });
                } else {
                    // menu type dropdowns don't update
                    selectAndClose({ value: activeItem });
                }
            }

            if (tabKey === e.key) {
                // if the filter input is currently focussed and we tab, then it should no longer be focussed
                // TODO: make this focus the clear button before moving to radios
                if (focusIsFilterInput) {
                    setIsFilterFocussed(false);

                    const radios = Array.from(
                        document.querySelectorAll<HTMLInputElement>(`[name="${menuName}"]:enabled`),
                    );
                    const radioToFocus =
                        radios.filter((item) => {
                            return item.value === focussedInputValue.current;
                        })[0] || radios[0];

                    if (radioToFocus) {
                        setFocussedInputValue(radioToFocus.value);
                        focusElementAndMaintainScrollPosition(radioToFocus);
                        selectItem({ value: radioToFocus.value, selectionTypes: [MenuTypes.Select] });
                    }
                } else if (focusIsMenuOptions || focusIsChildOfMenuOptions) {
                    setIsFilterFocussed(true);
                } else {
                    // keep focus within the menu, TODO: loop back to focus first item in menu
                    setIsFilterFocussed(true);
                }
                setInteractionMode(InteractionModes.Keyboard);
                return;
            }

            // if no active or focussed item and it's the up arrow, don't navigate
            if (!activeItem && !focusIsMenuOptions && !focusIsChildOfMenuOptions && e.key === arrowUp) {
                // do nothing
                return;
            }

            if (arrows.includes(e.key)) {
                // nav
                if (navArrows.includes(e.key)) {
                    e.preventDefault();
                    navigateItem(e);
                } else {
                    // do nothing
                    return;
                }
            }

            if (!isActiveItemVisible) {
                return;
            }

            // select the focussed item
            if (selectKeys.includes(e.key)) {
                if (menuType === MenuTypes.Select && activeItem === focussedInputValue.current) {
                    // select the active item for a select type dropdown
                    selectAndClose({ value: activeItem, selectionTypes: [MenuTypes.Select] });
                } else if (menuType === MenuTypes.NavMenu && activeItem === focussedInputValue.current) {
                    if (selectKeys.includes(e.key)) {
                        selectAndClose({
                            value: focussedInputValue.current,
                            selectionTypes: [MenuTypes.NavMenu],
                        });
                    }
                }
            }
        };

        const mouseMoveOnItem: React.MouseEventHandler = (e) => {
            // get the input - TODO: use refs
            const inputEl = e.currentTarget.querySelector('input');

            // focus the radio input
            setInteractionMode(InteractionModes.Mouse);
            if (inputEl) {
                setFocussedInputValue(inputEl.value);
                focusElementAndMaintainScrollPosition(inputEl);
            }
            if (isFilterFocussed) {
                // refocus the filter
                filterInputRef.current?.focus();
            }
        };

        const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setFilterValue(e.target.value);
        };

        const handleClearButton = () => {
            setFilterValue('');
            setIsFilterFocussed(true);
        };

        if (typeof focussedInputValue.current === 'undefined' && typeof activeItem !== 'undefined') {
            setFocussedInputValue(activeItem);
        }

        React.useLayoutEffect(() => {
            const inputEl = document.querySelector(
                `[name="${menuName}"][value="${focussedInputValue.current}"]`,
            ) as HTMLInputElement;
            if (inputEl) {
                focusElementAndMaintainScrollPosition(inputEl);
            } else {
                const firstInput = document.querySelector<HTMLInputElement>(`[name="${menuName}"]:enabled`);
                if (firstInput) {
                    setFocussedInputValue(firstInput.value);
                    focusElementAndMaintainScrollPosition(firstInput);
                }
            }

            if (isFilterFocussed) {
                // refocus the filter
                filterInputRef.current?.focus();
            }
        });

        React.useEffect(() => {
            if (!menuScrollY) {
                return;
            }
            // update the menuScrollY when the active item changes
            const inputEl = document.querySelector(`[name="${menuName}"][value="${activeItem}"]`) as HTMLInputElement;
            if (inputEl) {
                const scrollVal = getScrollToItem(inputEl, menuScrollY.current, menuOptionsRef);
                menuOptionsRef.current?.scrollTo(0, scrollVal);
                menuScrollY.current = scrollVal;
            }
        }, [activeItem, menuScrollY]);

        React.useLayoutEffect(() => {
            if (!menuScrollY) {
                return;
            }
            menuOptionsRef.current?.scrollTo(0, menuScrollY.current);
        });

        const mergedMenuRef = useMergedRef(ref, menuRef);

        /**
         * If the user clicks outside of the menu, close the menu.
         */
        useClickOutside(menuRef, () => close({ value: activeItem }));

        return (
            <Portal node={node} key={portalKey}>
                <StyledMenuList
                    ref={mergedMenuRef}
                    {...props}
                    style={style}
                    onKeyDown={keyDown}
                    key={menuId}
                    id={menuId}
                    data-test-id={dataId}
                    density={density}
                    fontWeight={fontWeight}
                >
                    {headerText && (
                        <StyledHeaderRow data-test-id={dataId + '-header'} density={density}>
                            {headerText}
                        </StyledHeaderRow>
                    )}
                    {hasFilter && (
                        <StyledSearchFilter
                            dataId={`${dataId}-multi-select`}
                            ref={filterInputRef as React.MutableRefObject<HTMLInputElement>}
                            placeholder={'Search...'}
                            handleChange={handleFilterChange}
                            handleClearButton={handleClearButton}
                            searchTerm={filterValue}
                            assistAccess={false}
                            data-test-id={`${dataId}-multi-select__filter`}
                            hasFocus={isFilterFocussed}
                        />
                    )}
                    <StyledMenuOptionsList
                        id={menuListId}
                        ref={menuOptionsRef as React.MutableRefObject<HTMLUListElement>}
                        hasFilter={hasFilter}
                        data-test-id={`${dataId}-list`}
                    >
                        {!!options?.length && (
                            <MenuOptions
                                dataId={dataId}
                                options={options}
                                interactionMode={interactionMode}
                                mouseDownOnItem={mouseDownHandler}
                                mouseMoveOnItem={mouseMoveOnItem}
                                clickOptionHandler={clickOptionHandler}
                                activeItem={activeItem}
                                menuName={menuName}
                                density={density}
                            />
                        )}
                        {!options?.length && <NoResultsText>No Results Found</NoResultsText>}
                    </StyledMenuOptionsList>
                </StyledMenuList>
            </Portal>
        );
    },
);

MenuList.displayName = 'MenuList';
